// import { useMsal } from "@azure/msal-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

export const postNewTrackHistory = async (trackHistory: {
	message: string;
	poNumber: string;
	postalCodeData?: ZipCode | null;
}) => {
	const url = apiRoutes.POSTTRACKHISTORY.replace(
		":poNumber",
		trackHistory.poNumber,
	);
	const resp = await postAndValidate(
		url,
		{
			remark: trackHistory.message,
			city: trackHistory.postalCodeData?.city.name,
			state: trackHistory.postalCodeData?.city?.state?.abbreviation,
			postalCode: trackHistory.postalCodeData?.postalCode,
			country: trackHistory.postalCodeData?.city?.state?.country?.abbreviation,
		},
		z.unknown(),
	);

	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePostNewTrackHistory = (options?: {
	onSuccess?: () => void;
}) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (trackHistory: {
			message: string;
			poNumber: string;
			postalCodeData?: ZipCode | null;
		}) => postNewTrackHistory(trackHistory),
		onSuccess: async () => {
			// Refetch the shipment so the newly created check call (with its
			// server-assigned status, icon and timestamp) is appended to the
			// tracking history. Awaiting keeps the mutation pending until the
			// refetch settles, so the spinner stays visible until the new message
			// is loaded.
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess("Tracking history added successfully.");
			options?.onSuccess?.();
		},
		onError: () => {
			showError("Failed to add tracking history. Please try again.");
		},
	});

	return {
		postNewTrackHistory: mutate,
		postNewTrackHistoryAsync: mutateAsync,
		isPostingNewTrackHistory: isPending,
		error,
		data,
		resetError: reset,
	};
};
